import React, { useMemo } from "react";
import { BookingResponseDto } from "../../api/bookingApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import {
  AlertCircle,
  ArrowRightLeft,
  Ban,
  Check,
  CheckCircle,
  Clock,
  CreditCard,
  MapPin,
  MessageSquareQuote,
  Music,
  User,
} from "lucide-react";
import { InstantRange } from "../../api/scheduleApi.ts";
import { format, Locale } from "date-fns";
import { Link } from "react-router";
import ChangeRequestBanner from "./ChangeRequestBanner.tsx";
import { useAuth } from "react-oidc-context";

const formatTimeSlot = (timeSlot: InstantRange, locale: Locale): string => {
  const formatTime = (date: Date) => {
    return format(date, "HH:mm", { locale });
  };

  const formatDate = (date: Date) => {
    return format(date, "EEE MMM d", { locale });
  };

  return `${formatDate(timeSlot.from)} ${formatTime(timeSlot.from)}-${formatTime(timeSlot.to)}`;
};

interface BookingCardProps {
  booking: BookingResponseDto;
  showTeacher: boolean;
  onAccept?: () => void;
  isAccepting?: boolean;
  showPaymentStatus?: boolean;
  isPastBooking?: boolean;
  cancellable?: boolean;
  movable?: boolean;
}

const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  showTeacher,
  onAccept,
  isAccepting,
  showPaymentStatus,
  isPastBooking,
  cancellable,
  movable,
}: BookingCardProps) => {
  const auth = useAuth();
  const currentUserId = auth.user?.profile.sub;
  const { dateFnsLocale } = useLanguageStore();
  const { language } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();
  const isCancelled = useMemo(() => {
    return booking.timeSlots.every(
      (timeSlot) => timeSlot.status == "CANCELLED",
    );
  }, [booking]);

  const name = useMemo(
    () => (showTeacher ? booking.teacherName : booking.bookerName),
    [booking],
  );
  const profilePictureUrl = useMemo(
    () =>
      showTeacher
        ? booking.teacherProfilePictureUrl
        : booking.bookerProfilePictureUrl,
    [booking],
  );

  const getProfilePicture = () => {
    return profilePictureUrl ? (
      <img
        src={profilePictureUrl}
        alt={""}
        className="h-4 w-4 rounded-full object-cover"
      />
    ) : (
      <User className="h-3.5 w-3.5" />
    );
  };

  const getColor = () => {
    if (booking.status === "CREATED") return "yellow";
    if (booking.status === "ACCEPTED") return "blue";
    return "green";
  };

  // Color class mappings for Tailwind
  const colorClasses = {
    yellow: {
      card: "border-yellow-200 bg-yellow-50 hover:border-yellow-300",
      icon: "text-yellow-600",
      status: "bg-yellow-100 text-yellow-800 border-yellow-300",
    },
    blue: {
      card: "border-soluud-primary-light bg-soluud-primary-subtle hover:border-soluud-primary",
      icon: "text-soluud-primary",
      status:
        "bg-soluud-primary-light text-soluud-primary-active border-soluud-primary",
    },
    green: {
      card: "border-green-200 bg-green-50 hover:border-green-300",
      icon: "text-green-600",
      status: "bg-green-100 text-green-800 border-green-300",
    },
  };

  const currentColor = getColor();
  const classes = colorClasses[currentColor as keyof typeof colorClasses];

  const getStatusIcon = () => {
    if (booking.status === "CREATED")
      return <AlertCircle className="h-3 w-3" />;
    if (booking.status === "ACCEPTED")
      return <CreditCard className="h-3 w-3" />;
    return <CheckCircle className="h-3 w-3" />;
  };

  const getStatusText = () => {
    if (booking.status === "CREATED") return "Pending";
    if (booking.status === "ACCEPTED") return "Awaiting Payment";
    return "Confirmed";
  };

  return (
    <div
      className={`rounded-lg border px-3 py-2 transition-all duration-200 ${classes.card}`}
    >
      {/* First Row: Instrument, Status, and Action Button */}
      <div className="mb-1.5 flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-1.5">
          <Music className={`h-4 w-4 flex-shrink-0 ${classes.icon}`} />
          <span className="truncate text-sm font-semibold">
            {getTranslations(
              supportedInstruments,
              booking.instrument,
              language,
            )}
          </span>
        </div>

        {showPaymentStatus && (
          <div className="flex flex-shrink-0 items-center gap-2">
            <div
              className={`flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium ${classes.status}`}
            >
              {getStatusIcon()}
              <span>{getStatusText()}</span>
            </div>
          </div>
        )}

        {isPastBooking && (
          <div className="flex flex-shrink-0 items-center gap-2">
            <div
              className={`flex items-center gap-1 rounded border px-0.5 py-0.5 ${classes.status}`}
            >
              <Link to={`/teachers/${booking.teacherId}/feedback`}>
                <MessageSquareQuote className={"h-4"} />
              </Link>
            </div>
          </div>
        )}
        {movable && !isCancelled && (
          <div className="flex flex-shrink-0 items-center gap-2">
            <div
              className={`text-soluud-primary flex items-center gap-1 rounded border px-0.5 py-0.5`}
            >
              <Link
                to={`/user/${currentUserId}/bookings/${booking.id}/move`}
                title="Move Booking"
              >
                <ArrowRightLeft className={"h-4"} />
              </Link>
            </div>
          </div>
        )}
        {cancellable && !isCancelled && (
          <div className="flex flex-shrink-0 items-center gap-2">
            <div
              className={`text-soluud-warning flex items-center gap-1 rounded border px-0.5 py-0.5`}
            >
              <Link
                to={`/user/${booking.bookerId}/bookings/${booking.id}/cancel`}
              >
                <Ban className={"h-4"} />
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Second Row: User and Location */}
      <div className="mb-1.5 flex items-center gap-3 text-xs text-gray-600">
        <div className="flex min-w-0 items-center gap-1">
          {getProfilePicture()}
          <span className="truncate">{name}</span>
        </div>
        <div className="flex min-w-0 items-center gap-1">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{booking.locationName}</span>
        </div>
      </div>

      {/* Change Request Banner */}
      {currentUserId && booking.status === "BOOKED" && (
        <div className="mb-1.5">
          <ChangeRequestBanner
            bookingId={booking.id}
            currentUserId={currentUserId}
          />
        </div>
      )}

      {/* Third Row+: Time Slots (stacked) */}
      <div className="flex justify-between">
        {booking.timeSlots.map((timeSlot, index) => {
          const classes = timeSlot.status == "CANCELLED" ? "line-through" : "";
          return (
            <div
              key={index}
              className={`flex items-center gap-1 text-xs text-gray-600`}
            >
              <Clock className="h-3 w-3 flex-shrink-0 text-gray-400" />
              <span className={classes}>
                {formatTimeSlot(timeSlot, dateFnsLocale)}
              </span>
            </div>
          );
        })}
        {onAccept && (
          <button
            onClick={onAccept}
            disabled={isAccepting}
            className="ml-2 flex items-center gap-1 rounded bg-green-600 px-2 py-0.5 text-xs font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
          >
            <Check className="h-3 w-3" />
            <span>{isAccepting ? "..." : "Accept"}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default BookingCard;
